import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ICandidateEventSubmissionControl } from 'app/models/Candidate/CandidateEventSubmissionControl';

export const candidateEventSubmissionControlSliceKey = 'candidateEventSubmissionControl';

export interface CandidateEventSubmissionControlState {
  items: ICandidateEventSubmissionControl[];
  placementControl: ICandidateEventSubmissionControl | null;
  requestedTravelerId: number | null;
  requestedPlacementId: number | null;
  loading: boolean;
  placementLoading: boolean;
  savingAffiliationIds: number[];
  listError: boolean;
  error: boolean;
}

export const initialState: CandidateEventSubmissionControlState = {
  items: [],
  placementControl: null,
  requestedTravelerId: null,
  requestedPlacementId: null,
  loading: false,
  placementLoading: false,
  savingAffiliationIds: [],
  listError: false,
  error: false,
};

const slice = createSlice({
  name: candidateEventSubmissionControlSliceKey,
  initialState,
  reducers: {
    fetchSubmissionControlsList(state, action: PayloadAction<{ travelerId: number }>) {
      if (state.requestedTravelerId !== action.payload.travelerId) {
        state.items = [];
      }
      state.requestedTravelerId = action.payload.travelerId;
      state.loading = true;
      state.listError = false;
      state.error = false;
    },
    fetchSubmissionControlsListSuccess(
      state,
      action: PayloadAction<{ travelerId: number; items: ICandidateEventSubmissionControl[] }>,
    ) {
      if (state.requestedTravelerId !== action.payload.travelerId) {
        return;
      }
      state.items = action.payload.items;
      state.loading = false;
      state.listError = false;
      state.error = false;
    },
    fetchSubmissionControlsListFailure(state, action: PayloadAction<{ travelerId: number }>) {
      if (state.requestedTravelerId !== action.payload.travelerId) {
        return;
      }
      state.items = [];
      state.loading = false;
      state.listError = true;
    },
    fetchSubmissionControlByPlacement(state, action: PayloadAction<{ placementId: number }>) {
      if (state.requestedPlacementId !== action.payload.placementId) {
        state.placementControl = null;
      }
      state.requestedPlacementId = action.payload.placementId;
      state.placementLoading = true;
      state.error = false;
    },
    fetchSubmissionControlByPlacementSuccess(
      state,
      action: PayloadAction<{ placementId: number; control: ICandidateEventSubmissionControl | null }>,
    ) {
      if (state.requestedPlacementId !== action.payload.placementId) {
        return;
      }
      state.placementControl = action.payload.control;
      state.placementLoading = false;
      state.error = false;
    },
    fetchSubmissionControlByPlacementFailure(state, action: PayloadAction<{ placementId: number }>) {
      if (state.requestedPlacementId !== action.payload.placementId) {
        return;
      }
      state.placementControl = null;
      state.placementLoading = false;
      state.error = true;
    },
    updateSubmissionPause(
      state,
      action: PayloadAction<{ travelerId: number; affiliationId: number; isSubmissionPaused: boolean }>,
    ) {
      if (!state.savingAffiliationIds.includes(action.payload.affiliationId)) {
        state.savingAffiliationIds.push(action.payload.affiliationId);
      }
      state.error = false;
    },
    updateSubmissionPauseSuccess(state, action: PayloadAction<ICandidateEventSubmissionControl>) {
      const index = state.items.findIndex(item => item.affiliationId === action.payload.affiliationId);
      if (index >= 0) {
        state.items[index] = {
          ...state.items[index],
          ...action.payload,
          eventName: action.payload.eventName ?? state.items[index].eventName,
        };
      } else {
        state.items.push(action.payload);
      }
      if (state.placementControl?.affiliationId === action.payload.affiliationId) {
        state.placementControl = {
          ...state.placementControl,
          ...action.payload,
          eventName: action.payload.eventName ?? state.placementControl.eventName,
        };
      }
      state.savingAffiliationIds = state.savingAffiliationIds.filter(
        id => id !== action.payload.affiliationId,
      );
      state.error = false;
    },
    updateSubmissionPauseFailure(state, action: PayloadAction<{ affiliationId: number }>) {
      state.savingAffiliationIds = state.savingAffiliationIds.filter(
        id => id !== action.payload.affiliationId,
      );
      state.error = true;
    },
    resetSubmissionControl(state) {
      state.items = [];
      state.placementControl = null;
      state.requestedTravelerId = null;
      state.requestedPlacementId = null;
      state.loading = false;
      state.placementLoading = false;
      state.savingAffiliationIds = [];
      state.listError = false;
      state.error = false;
    },
    resetPlacementSubmissionControl(state) {
      state.placementControl = null;
      state.requestedPlacementId = null;
      state.placementLoading = false;
    },
  },
});

export const {
  actions: candidateEventSubmissionControlActions,
  reducer: candidateEventSubmissionControlReducer,
} = slice;
